import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useDispatch, useSelector } from 'react-redux';
import { deleteItem, fetchItems, postItem, putItem } from '../../../support/queries';
import { RootState } from '../../../support/store';
import { checkCiscoProductsLoaded } from '../../../support/slices/productsSlice';
import { CacheKey } from '../../../types/cache-keys';
import { AdminDialog } from '../AdminDialog';
import {
  ModelConfig,
  isMultiSelectField,
  isSelectField,
  normalizeEndpoint,
} from '../RbacConfig';
import { AdminTable } from './AdminTable';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import useModelQuery from '../Hooks/useModelQuery';

interface DialogComponentProps<TEditItem> {
  open: boolean;
  onClose: () => void;
  editItem: TEditItem | null;
  readOnly?: boolean;
}

type AdminTabProps<TEditItem> = {
  listComponent?: React.ReactNode;
  DialogComponent?: React.ComponentType<DialogComponentProps<TEditItem>>;
  model: ModelConfig;
  actionButton?: React.ReactNode;
  editAction?: (row: number | 'new') => void;
  hideAdd?: boolean;
  canDeleteOverride?: boolean;
  canCloneOverride?: boolean;
  // Opt-in reporting surface: force the tab to read-only regardless of the caller's
  // RBAC, hiding every create/edit/delete/clone affordance and opening rows as view-only.
  // Used by the campaign admin tabs, where the work is owned and mutated by SEs in the
  // resource surface and admin is for reporting only. Off by default — does not change
  // the behavior of any existing caller.
  reportingOnly?: boolean;
};

const AdminTab = <TEditItem,>({
                                listComponent,
                                DialogComponent,
                                model,
                                actionButton,
                                editAction,
                                hideAdd = false,
                                canDeleteOverride,
                                canCloneOverride,
                                reportingOnly = false,
                              }: AdminTabProps<TEditItem>) => {
  const [editItem, setEditItem] = useState<TEditItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoadingEdit] = useState(false);
  const [assignmentTypeLookup, setAssignmentTypeLookup] = useState<Record<number, string>>({});
  const [userGroupLookup, setUserGroupLookup] = useState<Record<number, string>>({});

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const ciscoProductMap = useSelector(
    (state: RootState) => state.products.ciscoProductMap || {}
  );

  const {
    isAdminFull,
    scopeIds,
    canCreateModel,
    canDeleteModel,
    canUpdateModel,
    isReadOnlyModel,
  } = useAdminPermissions();

  const query = useModelQuery(model);

  useEffect(() => {
    if (model?.key === 'escalationProductSupport' || model?.key === 'escalations') {
      dispatch(checkCiscoProductsLoaded() as never);
    }
  }, [model?.key, dispatch]);

  useEffect(() => {
    if (model?.key !== 'escalationProductSupport') return;

    Promise.all([
      fetchItems('db/escalation-assignment-type'),
      fetchItems('db/user-group'),
    ]).then(([assignmentTypes, userGroups]) => {
      const nextAssignmentTypeLookup: Record<number, string> = {};
      const nextUserGroupLookup: Record<number, string> = {};

      (assignmentTypes as Array<{ id: number; name: string }>).forEach((item) => {
        nextAssignmentTypeLookup[item.id] = item.name;
      });

      (userGroups as Array<{ id: number | string; name: string }>).forEach((item) => {
        nextUserGroupLookup[Number(item.id)] = item.name;
      });

      setAssignmentTypeLookup(nextAssignmentTypeLookup);
      setUserGroupLookup(nextUserGroupLookup);
    });
  }, [model?.key]);

  // Every distinct `optionsEndpoint` across this model's select and multiselect fields.
  //
  // Before this existed, `selectOptions` was hard-coded for the `users` model alone, so any other
  // model's select field rendered an empty dropdown — which is unusable for the SAFE join tables,
  // where both columns of the row *are* foreign keys.
  const optionEndpoints = useMemo(() => {
    const seen = new Map<string, { url: string; params: Record<string, any> }>();

    (model?.fields || []).forEach((field) => {
      if (!isSelectField(field) && !isMultiSelectField(field)) return;
      if (!field.optionsEndpoint || seen.has(field.optionsEndpoint)) return;

      seen.set(field.optionsEndpoint, {
        url: field.optionsEndpoint,
        params: field.optionsParams || {},
      });
    });

    return Array.from(seen.values());
  }, [model?.fields]);

  // Only fetched once a dialog is open: option lists are for editing, and several of them (the
  // Cisco product list in particular) are far larger than the table being listed.
  const optionQueries = useQueries({
    queries: optionEndpoints.map((entry) => ({
      queryKey: [CacheKey.Admin, 'options', entry.url, entry.params],
      queryFn: () => fetchItems(entry.url, entry.params),
      staleTime: 5 * 60 * 1000,
      enabled: isDialogOpen,
    })),
  });

  // Rebuilt every render rather than memoised: it is a handful of array assignments, and the
  // alternative is a dependency on `optionQueries`, which is a fresh array on every render anyway.
  const selectOptions: Record<string, unknown[]> = {};

  optionEndpoints.forEach((entry, index) => {
    const data = optionQueries[index]?.data;
    if (Array.isArray(data)) selectOptions[entry.url] = data;
  });

  // The users model picks a manager out of the very list it is already showing, so reuse those
  // rows rather than waiting on a second identical request.
  if (model?.key === 'users' && !selectOptions['db/user']?.length) {
    selectOptions['db/user'] = (query?.data || []).map(
      (row: unknown) => row as Record<string, unknown>
    );
  }

  const handleEdit = (item: unknown) => {
    setEditItem(item as TEditItem | null);
    setIsDialogOpen(true);
  };

  const handleClone = (item: unknown) => {
    setEditItem({ ...(item as Record<string, unknown>), __clone__: true } as TEditItem);
    setIsDialogOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ model, id }: { model: ModelConfig; id: number | string }) => {
      const { url } = normalizeEndpoint(model.endpoint);
      return deleteItem(`${url}/${id}/`);
    },
    onSuccess: (_, { model }) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, model.key] });
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async ({ model, data }: { model: ModelConfig; data: Record<string, unknown> }) => {
      const { url } = normalizeEndpoint(model.endpoint);
      return data.id != null ? putItem(url, data) : postItem(url, data);
    },
    onSuccess: (_, { model }) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, model.key] });
    },
  });

  const handleGenericSave = (data: Record<string, unknown>) => {
    upsertMutation.mutate({ model, data });
    queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, model.key] });
    setIsDialogOpen(false);
    setEditItem(null);
  };

  const handleDelete = (currentModel: ModelConfig, id: number) => {
    if (!window.confirm('Are you sure?')) return;
    deleteMutation.mutate({ model: currentModel, id });
    queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, currentModel.key] });
  };

  const normalizedRows = useMemo(() => {
    const rows = (query?.data || []).map((r: unknown) => ({ ...(r as Record<string, unknown>) }));

    if (model?.key === 'escalationProductSupport') {
      return rows.map((r) => {
        const productValue = r.product;
        const assignmentTypeValue = r.assignment_type;
        const userGroupValue = r.user_group;

        const productId =
          typeof productValue === 'number'
            ? productValue
            : typeof productValue === 'object' && productValue !== null && 'id' in productValue
              ? Number((productValue as { id?: unknown }).id)
              : null;

        const assignmentTypeId =
          typeof assignmentTypeValue === 'number'
            ? assignmentTypeValue
            : typeof assignmentTypeValue === 'object' &&
            assignmentTypeValue !== null &&
            'id' in assignmentTypeValue
              ? Number((assignmentTypeValue as { id?: unknown }).id)
              : null;

        const userGroupId =
          typeof userGroupValue === 'number'
            ? userGroupValue
            : typeof userGroupValue === 'object' && userGroupValue !== null && 'id' in userGroupValue
              ? Number((userGroupValue as { id?: unknown }).id)
              : null;

        return {
          ...r,
          product_name:
            typeof r.product_name === 'string' && r.product_name.trim() !== ''
              ? r.product_name
              : productId != null
                ? ciscoProductMap[productId]?.name ?? String(productId)
                : '',
          assignment_type_name:
            typeof r.assignment_type_name === 'string' && r.assignment_type_name.trim() !== ''
              ? r.assignment_type_name
              : typeof assignmentTypeValue === 'string'
                ? assignmentTypeValue
                : assignmentTypeId != null
                  ? assignmentTypeLookup[assignmentTypeId] ?? String(assignmentTypeId)
                  : '',
          user_group_name:
            typeof r.user_group_name === 'string' && r.user_group_name.trim() !== ''
              ? r.user_group_name
              : typeof userGroupValue === 'string'
                ? userGroupValue
                : userGroupId != null
                  ? userGroupLookup[userGroupId] ?? String(userGroupId)
                  : '',
        };
      });
    }

    if (model?.key === 'escalations') {
      return rows.map((r) => {
        const productValue = r.product;
        const productId =
          typeof productValue === 'number'
            ? productValue
            : typeof productValue === 'object' && productValue !== null && 'id' in productValue
              ? Number((productValue as { id?: unknown }).id)
              : null;

        return {
          ...r,
          product_name:
            typeof r.product_name === 'string' && r.product_name.trim() !== ''
              ? r.product_name
              : productId != null
                ? ciscoProductMap[productId]?.name ?? String(productId)
                : '',
          requested_by_email:
            typeof r.requested_by === 'object' &&
            r.requested_by !== null &&
            'email' in r.requested_by
              ? String((r.requested_by as { email?: unknown }).email ?? '')
              : '',
        };
      });
    }

    if (model?.key === 'escalationHistory') {
      return rows.map((r) => ({
        ...r,
        escalation_id:
          typeof r.escalation === 'object' && r.escalation !== null && 'id' in r.escalation
            ? Number((r.escalation as { id?: unknown }).id)
            : typeof r.escalation === 'number'
              ? r.escalation
              : '',
        performed_by_email:
          typeof r.performed_by === 'object' &&
          r.performed_by !== null &&
          'email' in r.performed_by
            ? String((r.performed_by as { email?: unknown }).email ?? '')
            : '',
      }));
    }

    return rows;
  }, [query?.data, model?.key, ciscoProductMap, assignmentTypeLookup, userGroupLookup]);

  const visibleRows = useMemo(() => {
    const rows = normalizedRows;
    if (isAdminFull) return rows;
    if (!model.scopeKey) return rows;

    const allowed = new Set(scopeIds(model.scopeKey));
    return rows.filter((r) => {
      const rowId = r.id;
      return typeof rowId === 'number' && allowed.has(rowId);
    });
  }, [normalizedRows, isAdminFull, scopeIds, model.scopeKey]);

  const canClone =
    reportingOnly
      ? false
      : typeof canCloneOverride === 'boolean'
        ? canCloneOverride
        : false;

  // Reporting-only forces the whole tab read-only; otherwise fall back to the model's RBAC.
  const isReadOnly = reportingOnly || isReadOnlyModel(model);

  return (
    <section>
      {listComponent ?? (
        <AdminTable
          model={model}
          rows={visibleRows}
          loading={query?.isLoading || false}
          onAdd={() => {
            if (reportingOnly || hideAdd) return;
            if (!canCreateModel(model)) return;
            editAction ? editAction('new') : handleEdit(null);
          }}
          onEdit={(row) => {
            editAction ? editAction(row.id) : handleEdit(row);
          }}
          onClone={(row) => {
            if (reportingOnly || !DialogComponent) return;
            handleClone(row);
          }}
          onDelete={(row) => {
            if (reportingOnly) return;
            handleDelete(model, row.id);
          }}
          actionButton={actionButton}
          canAdd={reportingOnly || hideAdd ? false : canCreateModel(model)}
          canEdit={reportingOnly ? false : canUpdateModel(model)}
          canDelete={
            reportingOnly
              ? false
              : typeof canDeleteOverride === 'boolean'
                ? canDeleteOverride
                : canDeleteModel(model)
          }
          canClone={canClone}
          readOnly={isReadOnly}
          emptyLabel={
            model.scopeKey &&
            !isAdminFull &&
            scopeIds(model.scopeKey).length === 0
              ? 'No records available (no scope access).'
              : undefined
          }
        />
      )}

      {DialogComponent && (
        <DialogComponent
          open={isDialogOpen}
          editItem={editItem}
          readOnly={isReadOnly}
          onClose={() => {
            setIsDialogOpen(false);
            setEditItem(null);
          }}
        />
      )}

      {!DialogComponent && (
        <AdminDialog
          open={isDialogOpen}
          model={model}
          fields={model?.fields || []}
          data={(editItem as Record<string, unknown>) || {}}
          setData={(value) => setEditItem(value as TEditItem)}
          onSave={() => handleGenericSave((editItem as Record<string, unknown>) || {})}
          onCancel={() => {
            setIsDialogOpen(false);
            setEditItem(null);
          }}
          isSaving={upsertMutation.isPending}
          selectOptions={selectOptions}
          isLoading={isLoadingEdit}
          readOnly={isReadOnly}
        />
      )}
    </section>
  );
};

export default AdminTab;